import React, { useState, useEffect } from 'react';
import { apiService } from '../../services';
import { LoadingSpinner } from '../ui';
import ArrowRight from 'lucide-react/icons/arrow-right';
import Check from 'lucide-react/icons/check';

interface UpgradeEligibility {
  canUpgrade: boolean;
  currentTier: string;
  upgradeTo?: string;
  upgradePrice?: number;
  originalPrice?: number;
  benefits?: string[];
  priceDisplay?: string;
  originalPriceDisplay?: string;
  reason?: string;
}

interface UpgradePromptProps {
  customerTier: string;
  onUpgradeClick: () => void;
}

export const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  customerTier,
  onUpgradeClick
}) => {
  const [eligibility, setEligibility] = useState<UpgradeEligibility | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkEligibility = async () => {
      try {
        setLoading(true);
        const response = await apiService.checkUpgradeEligibility();
        setEligibility(response);
      } catch (err) {
        setError('Failed to check upgrade eligibility');
        console.error('Upgrade eligibility check failed:', err);
      } finally {
        setLoading(false);
      }
    };

    checkEligibility();
  }, [customerTier]);

  // Don't show anything while loading
  if (loading) {
    return null;
  }

  // Don't show if there's an error or customer can't upgrade
  if (error || !eligibility?.canUpgrade) {
    return null;
  }

  // Enterprise customers see nothing (already at highest tier)
  if (customerTier === 'enterprise') {
    return null;
  }

  const getTierDisplayName = (tier: string) => {
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };

  return (
    <div className="mt-8">
      <h2 className="text-xl font-semibold mb-4">Upgrade Your Tier</h2>
      <div className="p-6 bg-secondary/5 rounded-lg border border-tertiary">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-2">
              {getTierDisplayName(eligibility.upgradeTo || '')} Edition
            </h3>

          <p className="text-secondary mb-4">
            {customerTier === 'essential'
              ? 'Expand your access with premium samples and early chapter releases.'
              : 'Share access with your team and get expert consultation.'
            }
          </p>

          {eligibility.benefits && eligibility.benefits.length > 0 && (
            <ul className="space-y-2 mb-6">
              {eligibility.benefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                  <span className="text-secondary">{benefit}</span>
                </li>
              ))}
            </ul>
          )}

          <button
            onClick={onUpgradeClick}
            className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-white rounded-lg font-medium hover:brightness-110 transition-all"
          >
            Upgrade to {getTierDisplayName(eligibility.upgradeTo || '')}
            {eligibility.priceDisplay && (
              <span className="ml-1">({eligibility.priceDisplay})</span>
            )}
            <ArrowRight className="w-4 h-4" />
          </button>

          {eligibility.originalPriceDisplay && eligibility.priceDisplay && (
            <div className="mt-3 p-3 bg-accent/10 rounded-lg">
              <p className="text-sm text-secondary">
                <span className="font-medium">Upgrade pricing:</span>
              </p>
              <p className="text-sm mt-1">
                <span className="line-through text-secondary">{eligibility.originalPriceDisplay}</span>
                <span className="mx-2 text-accent font-bold">{eligibility.priceDisplay}</span>
                <span className="text-green-600 font-medium">
                  Save ${((eligibility.originalPrice || 0) - (eligibility.upgradePrice || 0)) / 100}
                </span>
              </p>
              <p className="text-xs text-secondary mt-1">
                Your {getTierDisplayName(customerTier)} purchase credit applied
              </p>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradePrompt;